import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  FolderKanban,
  ListTodo,
  Clock,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Loader2,
  Plus,
  CalendarRange
} from 'lucide-react';

function TrendBadge({ current, previous, label = 'sem. préc.' }) {
  if (previous == null || previous === 0) return null;
  const diff = ((current - previous) / previous * 100);
  const isUp = diff >= 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium mt-1 ${isUp ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isUp ? '+' : ''}{diff.toFixed(0)}% vs {label}
    </span>
  );
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { getAuthHeader, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWorkItemTypeClass = (type) => {
    const classes = {
      'Epic': 'work-item-epic',
      'Feature': 'work-item-feature',
      'UserStory': 'work-item-userstory',
      'Task': 'work-item-task',
      'Bug': 'work-item-bug'
    };
    return classes[type] || 'bg-muted text-muted-foreground';
  };

  const getStateClass = (state) => {
    if (['Done', 'Closed', 'Completed'].includes(state)) return 'state-done';
    if (['Active', 'In Progress'].includes(state)) return 'state-active';
    return 'state-new';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-extrabold">
            Bonjour, {user?.name?.split(' ')[0] || 'Utilisateur'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Voici un aperçu de votre activité DevOps
          </p>
        </div>
        <Link to="/work-items/new">
          <Button className="rounded-sm" data-testid="new-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Tâche
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="kpi-card" data-testid="kpi-projects">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Projets</p>
              <p className="kpi-value mt-2">{stats?.projects_count || 0}</p>
            </div>
            <div className="p-3 bg-primary/10 rounded-sm">
              <FolderKanban className="h-6 w-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card" data-testid="kpi-tasks">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Tâches</p>
              <p className="kpi-value mt-2">{stats?.tasks_count || 0}</p>
            </div>
            <div className="p-3 bg-yellow-500/10 rounded-sm">
              <ListTodo className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card" data-testid="kpi-hours">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Heures cette semaine</p>
              <p className="kpi-value mt-2">{stats?.this_week_hours || 0}h</p>
              <TrendBadge current={stats?.this_week_hours || 0} previous={stats?.last_week_hours} />
            </div>
            <div className="p-3 bg-blue-500/10 rounded-sm">
              <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card" data-testid="kpi-month-hours">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Heures ce mois</p>
              <p className="kpi-value mt-2">{stats?.this_month_hours || 0}h</p>
              <TrendBadge current={stats?.this_month_hours || 0} previous={stats?.last_month_hours} label="mois préc." />
            </div>
            <div className="p-3 bg-purple-500/10 rounded-sm">
              <CalendarRange className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card className="kpi-card" data-testid="kpi-completed">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Complétées</p>
              <p className="kpi-value mt-2">{stats?.completed_count || 0}</p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-sm">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Tasks & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tasks */}
        <Card className="lg:col-span-2" data-testid="recent-tasks-card">
          <CardHeader className="border-b border-border bg-muted/20 p-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">Tâches Récentes</CardTitle>
              <Link to="/work-items">
                <Button variant="ghost" size="sm" className="text-primary">
                  Voir tout
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {stats?.recent_tasks?.length > 0 ? (
              <div className="divide-y divide-border">
                {stats.recent_tasks.slice(0, 5).map((task) => (
                  <Link 
                    key={task.id} 
                    to={`/work-items/${task.id}`}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant="outline" className={`shrink-0 ${getWorkItemTypeClass(task.work_item_type)}`}>
                        {task.work_item_type}
                      </Badge>
                      <span className="truncate font-medium">{task.title}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="secondary" className={getStateClass(task.state)}>
                        {task.state}
                      </Badge>
                      {task.estimated_hours > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {task.completed_hours || 0}/{task.estimated_hours}h
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="empty-state py-12">
                <ListTodo className="empty-state-icon" />
                <p className="empty-state-title">Aucune tâche</p>
                <p className="empty-state-description">
                  Commencez par créer votre première tâche
                </p>
                <Link to="/work-items/new" className="mt-4">
                  <Button className="rounded-sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une tâche
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Time Summary */}
        <Card data-testid="time-summary-card">
          <CardHeader className="border-b border-border bg-muted/20 p-4">
            <CardTitle className="text-lg font-heading">Résumé du temps</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total des heures</span>
                  <span className="text-2xl font-bold font-heading">{stats?.total_hours || 0}h</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((stats?.total_hours || 0) / 100 * 100, 100)}%` }}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Cette semaine</span>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="font-semibold">{stats?.this_week_hours || 0}h</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">En cours</span>
                  <span className="font-semibold">{stats?.in_progress_count || 0} tâches</span>
                </div>
              </div>

              <Link to="/time-entries" className="block pt-4">
                <Button variant="outline" className="w-full rounded-sm">
                  Voir les détails
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
